/**
 * Color utility functions for team colors
 * Generates deterministic colors based on team name and ID
 */

/**
 * Hash a string to a number
 * This creates a deterministic hash that will always be the same for the same input
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Generate a deterministic color for a team based on name and ID
 * Uses HSL color space for better color distribution
 * @param teamName - The team's name
 * @param teamId - The team's ID
 * @returns Hex color code (e.g., "#FF5733")
 */
export function generateTeamColor(teamName: string, teamId: number): string {
  // Combine name and ID for the hash
  const combined = `${teamName}-${teamId}`;
  const hash = hashString(combined);

  // Generate hue (0-360) - full color spectrum
  const hue = hash % 360;

  // Use good saturation (60-75%) for vibrant but not overwhelming colors
  const saturation = 60 + (hash % 16);

  // Use medium lightness (40-55%) for good contrast on white backgrounds
  const lightness = 40 + (hash % 16);

  // Convert HSL to hex
  return hslToHex(hue, saturation, lightness);
}

/**
 * Convert HSL color values to hex color code
 * @param h - Hue (0-360)
 * @param s - Saturation (0-100)
 * @param l - Lightness (0-100)
 * @returns Hex color code
 */
function hslToHex(h: number, s: number, l: number): string {
  const sDecimal = s / 100;
  const lDecimal = l / 100;

  const c = (1 - Math.abs(2 * lDecimal - 1)) * sDecimal;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lDecimal - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (h >= 300 && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  const rHex = Math.round((r + m) * 255)
    .toString(16)
    .padStart(2, "0");
  const gHex = Math.round((g + m) * 255)
    .toString(16)
    .padStart(2, "0");
  const bHex = Math.round((b + m) * 255)
    .toString(16)
    .padStart(2, "0");

  return `#${rHex}${gHex}${bHex}`;
}

/**
 * Get the color for a team, using their assigned color or generating one deterministically
 * @param team - Team object with optional colour_code, name, and id
 * @returns Hex color code
 */
export function getTeamColor(team: {
  colour_code?: string;
  name: string;
  id: number;
}): string {
  // Use assigned color if available and valid
  if (team.colour_code && /^#[0-9A-F]{6}$/i.test(team.colour_code)) {
    return team.colour_code;
  }

  // Otherwise generate deterministic color
  return generateTeamColor(team.name, team.id);
}
